import { useTranslation } from "react-i18next";
import type { Session } from "../../../../../store/admin/sessionsStore";
import {
  DENSE_ROW,
  ICON_BTN,
  ICON_BTN_DANGER,
  STATUS_CHIP,
  type ThemeClasses,
} from "../../../shared";
import {
  formatLocation,
  getStatusInfo,
} from "../../../../../store/admin/sessionsStore";
import { calculateDuration, formatDate } from "../../../../../utils/getDate";
import useIsMonitor from "../../../../../hooks/useIsMonitor";
interface SessionRowProps {
  session: Session;
  themeClasses: ThemeClasses;
  onView: () => void;
  onLogout: () => void;
}

const getPlatformInfo = (
  platform: string,
): { icon: string; color: string; bgColor: string; label: string } => {
  const platformLower = platform?.toLowerCase() || "";

  if (
    platformLower.includes("ios") ||
    platformLower.includes("iphone") ||
    platformLower.includes("ipad")
  ) {
    return {
      icon: "ri-apple-line",
      color: "text-[#F97316]",
      bgColor: "bg-[#F973161A]",
      label: "iOS App",
    };
  }

  if (platformLower.includes("android")) {
    return {
      icon: "ri-android-line",
      color: "text-blue-500",
      bgColor: "bg-blue-50",
      label: "Android App",
    };
  }

  if (platformLower.includes("web")) {
    return {
      icon: "ri-computer-line",
      color: "text-purple-500",
      bgColor: "bg-purple-50",
      label: "Web Dashboard",
    };
  }

  return {
    icon: "ri-device-line",
    color: "text-gray-500",
    bgColor: "bg-gray-100",
    label: platform || "Unknown",
  };
};

export default function SessionRow({
  session,
  themeClasses,
  onView,
  onLogout,
}: SessionRowProps) {
  const { t } = useTranslation("adminSections");
  const { isMonitor, isReadOnly } = useIsMonitor();
  const platformInfo = getPlatformInfo(session.platform);
  const statusInfo = getStatusInfo(session.active, session.endedAt, t);
  const location = formatLocation(session.location);
  const duration = calculateDuration(session.createdAt, session.endedAt);

  const roleUser = {
    admin: t("sessions.roles.admin"),
    driver: t("sessions.roles.driver"),
    monitor: t("sessions.roles.monitor"),
    "admin reseller": t("sessions.roles.adminReseller"),
  };

  const neutralBtn = `${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`;
  const startTime = session.createdAt ? formatDate(session.createdAt._seconds) : "-";
  // Etiqueta arriba y valor abajo en columnas de ancho fijo: antes eran un
  // flex-wrap que, con valores largos, partía la fila en dos o tres renglones.
  const col = (label: string, value: string, width: string) => (
    <div className={`${width} shrink-0 min-w-0`} title={`${label}: ${value}`}>
      <p className={`text-[11px] ${themeClasses.textSecondary} truncate`}>{label}</p>
      <p className={`text-sm ${themeClasses.text} truncate`}>{value}</p>
    </div>
  );

  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
      {/* Avatar con icono de plataforma */}
      <div
        className={`w-9 h-9 rounded-lg ${platformInfo.bgColor} flex items-center justify-center flex-shrink-0`}
      >
        <i className={`${platformInfo.icon} text-base ${platformInfo.color}`}></i>
      </div>

      {/* Usuario Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`font-semibold ${themeClasses.text} text-sm truncate`}>
            {session.userName || "Usuario"}
          </span>
          <span className={`${STATUS_CHIP} ${statusInfo.bg} ${statusInfo.text}`}>
            {statusInfo.label}
          </span>
        </div>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {session.userEmail} •{" "}
          {roleUser[session.role as keyof typeof roleUser] ||
            t("sessions.roles.user")}
        </p>
      </div>

      {/* Columnas informativas: se ocultan de derecha a izquierda */}
      <div className="hidden md:flex items-center gap-4 shrink-0">
        {col(t("sessions.fields.device"), session.device || "-", "w-28")}
        {col(t("sessions.fields.duration"), duration, "w-20")}
      </div>
      <div className="hidden xl:flex items-center gap-4 shrink-0">
        {col(t("sessions.fields.location"), location, "w-32")}
        {col("IP", session.ip || "-", "w-28")}
        {col(t("sessions.fields.startTime"), startTime, "w-32")}
      </div>

      {/* Acciones */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          aria-label={t("general.viewDetails", { ns: "common" })}
          title={t("general.viewDetails", { ns: "common" })}
          onClick={onView}
          className={neutralBtn}
        >
          <i className="ri-eye-line"></i>
        </button>
        {session.active && !isMonitor && !isReadOnly && (
          <button
            aria-label={t("sessions.actions.logout")}
            title={t("sessions.actions.logout")}
            onClick={onLogout}
            className={ICON_BTN_DANGER}
          >
            <i className="ri-logout-box-line"></i>
          </button>
        )}
      </div>
    </div>
  );
}
